import {inject, Injectable} from '@angular/core';
import {catchError, forkJoin, map, Observable, of, switchMap, take, takeWhile, timeout, timer} from 'rxjs';
import {
  ConnectProviderFreeFieldKey,
  ConnectRun,
  ConnectWorkflow,
  ConnectWorkflowNode,
  ConnectWorkflowsService,
} from '../../../services/connect/connect-workflows.service';
import {
  projectConnectGuidedConnectorOperation,
  ConnectNodesService,
} from '../../../services/connect/connect-nodes.service';
import {ConnectGuidedWorkflowJsonAdapterSession} from '../guided/connect-guided-workflow-json-adapter';

type JsonSchemaProperty = {
  format?: string;
  enum?: string[];
  default?: string;
  title?: string;
};

type AiExportMetadata = {
  enabled?: boolean;
  operation_id?: string;
  trigger_node_id?: string;
  description?: string;
  input_schema?: {
    properties?: Record<string, JsonSchemaProperty>;
    required?: string[];
  };
};

export type ConnectFunctionField = {
  key: string;
  label: string;
  required: boolean;
  format?: string;
  options: string[];
  defaultValue: string;
  multiline: boolean;
};

export type ConnectFunctionDefinition = {
  workflowId: string;
  triggerNodeId: string;
  operationId: string;
  title: string;
  description: string;
  icon: string;
  fields: ConnectFunctionField[];
};

export type LocalConnectRun = Pick<ConnectRun, 'id' | 'status' | 'steps'>;

export type ConnectProviderFreeFieldOption = {
  key: ConnectProviderFreeFieldKey;
  label: string;
  help: string;
};

export type ConnectGuidedWorkflowSummary = {
  workflowId: string;
  title: string;
};

export const CONNECT_PROVIDER_FREE_FIELDS: ConnectProviderFreeFieldOption[] = [
  {key: 'full_name', label: 'Full name', help: 'The person’s name'},
  {key: 'work_email', label: 'Work email', help: 'A work email address'},
  {key: 'company', label: 'Company', help: 'The company name'},
  {key: 'country', label: 'Country', help: 'The relevant country'},
  {key: 'question', label: 'Question', help: 'A short question'},
  {key: 'details', label: 'Details', help: 'A longer explanation'},
];

@Injectable({providedIn: 'root'})
export class ConnectNoviceLocalService {
  private readonly workflows = inject(ConnectWorkflowsService);
  private readonly nodes = inject(ConnectNodesService);

  loadFunctions(): Observable<ConnectFunctionDefinition[]> {
    return this.workflows.listWorkflows({tags: ['provider-free'], take: 100}).pipe(
      map(workflows => workflows
        .map(workflow => this.toFunction(workflow))
        .filter((value): value is ConnectFunctionDefinition => value !== null)
        .sort((left, right) => left.title.localeCompare(right.title))),
    );
  }

  loadGuidedWorkflows(): Observable<ConnectGuidedWorkflowSummary[]> {
    return forkJoin({
      catalog: this.nodes.fetchCatalog().pipe(catchError(() => of(null))),
      workflows: this.workflows.listWorkflows({tags: ['guided'], take: 100}),
    }).pipe(
      map(({catalog, workflows}) => {
        const projector = catalog
          ? (node: ConnectWorkflowNode, providerRef: string) =>
              projectConnectGuidedConnectorOperation(node, providerRef, catalog.nodes)
          : undefined;
        return workflows
          .filter(workflow => ConnectGuidedWorkflowJsonAdapterSession.open(
            workflow,
            projector,
            catalog?.nodes,
          ) !== null)
          .filter(workflow => typeof workflow.id === 'string' && workflow.id.length > 0)
          .map(workflow => ({workflowId: workflow.id as string, title: workflow.name.trim()}))
          .filter(workflow => workflow.title.length > 0)
          .sort((left, right) => left.title.localeCompare(right.title));
      }),
    );
  }

  createFunction(name: string, fieldKeys: ConnectProviderFreeFieldKey[]): Observable<ConnectFunctionDefinition> {
    return this.workflows.createProviderFreeFunction({name: name.trim(), fields: fieldKeys}).pipe(
      map(workflow => {
        const definition = this.toFunction(workflow);
        if (!definition) throw new Error('Connect returned an invalid function.');
        return definition;
      }),
    );
  }

  run(definition: ConnectFunctionDefinition, input: Record<string, unknown>): Observable<LocalConnectRun> {
    return this.workflows.runTrigger(definition.workflowId, definition.triggerNodeId, input).pipe(
      switchMap(({runId}) => timer(0, 1000).pipe(
        switchMap(() => this.workflows.getRun(runId).pipe(timeout({first: 20_000}))),
        take(60),
        takeWhile(run => !['succeeded', 'failed', 'cancelled', 'timed_out'].includes(run.status), true),
      )),
    );
  }

  private toFunction(workflow: ConnectWorkflow): ConnectFunctionDefinition | null {
    const workflowId = typeof workflow.id === 'string' ? workflow.id.trim() : '';
    const metadata = workflow['ai_export'] as AiExportMetadata | undefined;
    const operationId = metadata?.operation_id?.trim() ?? '';
    const triggerNodeId = metadata?.trigger_node_id?.trim() ?? '';
    const properties = metadata?.input_schema?.properties;
    if (!workflowId || metadata?.enabled !== true || !operationId || !triggerNodeId || !properties) return null;

    const required = new Set(metadata.input_schema?.required ?? []);
    const fields = Object.entries(properties).map(([key, property]) => ({
      key,
      label: property.title?.trim() || this.humanize(key),
      required: required.has(key),
      format: property.format,
      options: Array.isArray(property.enum) ? property.enum : [],
      defaultValue: typeof property.default === 'string' ? property.default : '',
      multiline: ['question', 'details', 'description', 'message'].some(part => key.toLowerCase().includes(part)),
    }));

    return {
      workflowId,
      triggerNodeId,
      operationId,
      title: workflow.name.trim(),
      description: metadata.description?.trim() || 'Run this configured function safely.',
      icon: this.iconFor(operationId),
      fields,
    };
  }

  private humanize(value: string): string {
    const spaced = value.replace(/[_-]+/g, ' ').trim();
    return spaced ? spaced[0].toUpperCase() + spaced.slice(1) : value;
  }

  private iconFor(operationId: string): string {
    const value = operationId.toLowerCase();
    if (value.includes('company')) return 'business';
    if (value.includes('support')) return 'support_agent';
    return 'person_add';
  }
}
